import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HandCoins } from 'lucide-react';
import {
  PERMISSIONS,
  SUPPLIER_PAYMENT_METHODS,
  SupplierPaymentStatus,
  type SupplierPaymentMethod,
  type SupplierPaymentSummary,
} from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { usePayableSuppliersQuery, useSupplierPaymentsQuery } from '../api/payables.api';
import { SupplierPaymentStatusBadge } from '../components/PayableBadges';

const PAGE_SIZE = 20;

/**
 * Every supplier payment, newest first.
 *
 * A voided payment still lists, with its amount intact and its status saying plainly what it is —
 * the history of what was paid and then withdrawn is the whole point of never deleting one.
 *
 * Search reaches the SPAY reference and the supplier's name. It does not reach the provider's own
 * reference, which would put a bank or card reference into the address bar of whoever searched.
 */
export function SupplierPaymentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branches } = useBranch();
  const { format } = useCurrency();
  const { can } = usePermissions();

  const [search, setSearch] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [method, setMethod] = useState<SupplierPaymentMethod | ''>('');
  const [status, setStatus] = useState<SupplierPaymentStatus | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = useSupplierPaymentsQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    supplierId,
    branchId,
    method,
    status,
    dateFrom,
    dateTo,
  });

  const suppliers = usePayableSuppliersQuery({ branchId });
  const canPay = can(PERMISSIONS.PAYABLES_PAY);

  const onFilter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const columns: Column<SupplierPaymentSummary>[] = [
    {
      key: 'reference',
      header: t('payables.fields.paymentReference'),
      primary: true,
      render: (payment) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {payment.reference}
          </span>
          <span className="text-muted text-xs" dir="auto">
            {payment.supplierName}
          </span>
        </span>
      ),
    },
    {
      key: 'branch',
      header: t('payables.fields.branch'),
      render: (payment) => (
        <span className="text-muted text-sm" dir="auto">
          {payment.branchName}
        </span>
      ),
    },
    {
      key: 'paymentDate',
      header: t('payables.fields.paymentDate'),
      render: (payment) => (
        <span className="text-ink text-sm" dir="ltr">
          {payment.paymentDate}
        </span>
      ),
    },
    {
      key: 'method',
      header: t('payables.fields.method'),
      render: (payment) => (
        <span className="text-muted text-sm">{t(`payables.methods.${payment.method}`)}</span>
      ),
    },
    {
      key: 'amount',
      header: t('payables.fields.amount'),
      align: 'end',
      render: (payment) => (
        <span className="flex flex-col items-end">
          <span
            className={
              payment.status === SupplierPaymentStatus.VOIDED
                ? 'text-muted line-through'
                : 'text-ink font-medium'
            }
            dir="ltr"
          >
            {format(payment.amountMinor)}
          </span>
          <span className="text-muted text-xs">
            {t('payables.allocationCount', { count: payment.allocationCount })}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('payables.fields.paymentStatus'),
      render: (payment) => <SupplierPaymentStatusBadge status={payment.status} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (payment) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('payables.openPaymentNamed', { reference: payment.reference })}
          onClick={() => void navigate(`/finance/payables/payments/${payment.id}`)}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('payables.payments.title')}
        subtitle={t('payables.payments.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => void navigate('/finance/payables')}>
              {t('payables.backToDashboard')}
            </Button>
            {canPay ? (
              <Button onClick={() => void navigate('/finance/payables/payments/new')}>
                {t('payables.recordPayment')}
              </Button>
            ) : null}
          </div>
        }
      />

      <Card>
        <CardHeader
          title={t('payables.payments.title')}
          subtitle={
            data ? t('payables.payments.count', { count: data.pagination.totalItems }) : undefined
          }
        />

        <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            type="search"
            value={search}
            onChange={(event) => onFilter(setSearch)(event.target.value)}
            placeholder={t('payables.payments.searchPlaceholder')}
            aria-label={t('payables.payments.searchLabel')}
          />
          <Select
            options={[
              { value: '', label: t('payables.filters.allSuppliers') },
              ...(suppliers.data ?? []).map((supplier) => ({
                value: supplier.supplierId,
                label: supplier.supplierName,
              })),
            ]}
            value={supplierId}
            aria-label={t('payables.fields.supplier')}
            onChange={(event) => onFilter(setSupplierId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('payables.filters.allBranches') },
              ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
            ]}
            value={branchId}
            aria-label={t('payables.fields.branch')}
            onChange={(event) => onFilter(setBranchId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('payables.filters.allMethods') },
              ...SUPPLIER_PAYMENT_METHODS.map((value) => ({
                value,
                label: t(`payables.methods.${value}`),
              })),
            ]}
            value={method}
            aria-label={t('payables.fields.method')}
            onChange={(event) =>
              onFilter(setMethod)(event.target.value as SupplierPaymentMethod | '')
            }
          />
          <Select
            options={[
              { value: '', label: t('payables.filters.allStatuses') },
              ...Object.values(SupplierPaymentStatus).map((value) => ({
                value,
                label: t(`payables.paymentStatus.${value}`),
              })),
            ]}
            value={status}
            aria-label={t('payables.fields.paymentStatus')}
            onChange={(event) =>
              onFilter(setStatus)(event.target.value as SupplierPaymentStatus | '')
            }
          />
          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => onFilter(setDateFrom)(event.target.value)}
            aria-label={t('payables.filters.dateFrom')}
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => onFilter(setDateTo)(event.target.value)}
            aria-label={t('payables.filters.dateTo')}
          />
        </div>

        <DataTable
          caption={t('payables.payments.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(payment) => payment.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('payables.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={HandCoins}
          emptyTitle={t('payables.payments.empty')}
          emptyDescription={t('payables.payments.emptyHint')}
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>
    </div>
  );
}
